import React, { useEffect, useMemo, useState } from "react";

import {
  LabeledSelect,
  Row,
  Col,
  Label,
  Button,
  AlertNotification,
  Text,
  Popup,
  PopupProps,
  Loading,
  Select,
} from "@commonsku/styles";

import EmailList from "../../EmailList";
import FormPopup from "../../FormPopup";
import { BASE_ZINDEX } from "../../../popup-factory";

import { useScheduledReports } from "./useScheduledReport";
import { closePopup } from "../../../actions/popup";
import { useDispatch } from "react-redux";
import { ScheduleFrequency, ScheduleFrequencyType } from "../../../types/scheduled_reports";
import {
  FREQUENCY_OPTIONS,
  DAYS_OF_WEEK,
  DAYS_OF_MONTH,
  QUARTER_FREQUENCY_OPTIONS,
} from "./types";

interface ScheduleReportPopupProps {
  onClose: () => void;
  report_id?: string;
}
interface ConfirmUnschedulePopupProps extends PopupProps {
  onClose: () => void;
  onConfirm: () => void;
  message: string;
}

const ConfirmUnschedulePopup = ({
  onClose,
  onConfirm,
  message,
  ...popupProps
}: ConfirmUnschedulePopupProps) => (
  <Popup width="438px" height="auto" noHeader {...popupProps}>
    <Row mb={24}>
      <Col>
        <Text color="neutrals" style={{ fontSize: 18, lineHeight: "28px" }}>
          {message}
        </Text>
      </Col>
    </Row>
    <Row flex>
      <Col center>
        <Button mr={24} onClick={onClose} variant="secondary">
          Cancel
        </Button>
        <Button variant="error" onClick={onConfirm}>
          Unschedule
        </Button>
      </Col>
    </Row>
  </Popup>
);

const ScheduleReportPopup = ({
  onClose,
  report_id = "",
}: ScheduleReportPopupProps) => {
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const dispatch = useDispatch();

  const {
    state: { schedule, success, isValid, isNew, loading },
    operations: { saveScheduledReport, unscheduleReport },
    updater,
    options: { savedReportOptions, sendToOptions },
    selections: { reportOption, monthDay, weekDay, quarterDay, sendTo },
  } = useScheduledReports(report_id);
  const scheduleFrequencyValue = useMemo(() => {
    return FREQUENCY_OPTIONS.find(
      (option) => option.value === schedule.frequency
    );
  }, [schedule.frequency]);

  const [showDayPicker, setShowDayPicker] = useState<boolean>(false);

  useEffect(() => {
    if (
      showDayPicker === false &&
      !!scheduleFrequencyValue &&
      scheduleFrequencyValue?.value !== ScheduleFrequency.DAY
    ) {
      setShowDayPicker(true);
    }
  }, [scheduleFrequencyValue, showDayPicker]);

  const isEditable = true;

  const handleClose = () => {
    updater.reset();
    if (onClose) {
      onClose();
      return;
    }
    dispatch(closePopup());
  };

  const handleLeftArrowClick = useMemo(
    () => (reportOption && !report_id ? updater.reset : null),
    [reportOption, report_id, updater]
  );

  const loadingContent = <Loading mt={120} />;

  const handleFrequencyChange = (option: {
    label: string;
    value: ScheduleFrequencyType;
  }) => {
    updater.setFrequency(option.value);
    setShowDayPicker(true);
  };
  const formContent = (
    <>
      <Row>
        <Col>
          <LabeledSelect
            inPopup
            label="Saved Report"
            options={savedReportOptions}
            value={reportOption}
            onChange={(option: any) => updater.setReportId(option.value)}
            isDisabled={
              !isNew ||
              (!!report_id &&
                savedReportOptions.find((report) => report.value === report_id))
            }
          />
        </Col>
      </Row>
      {reportOption && (
        <>
          <Row mt={16}>
            <Col mr={16}>
              <Label>Frequency</Label>
              <Select
                value={scheduleFrequencyValue}
                options={FREQUENCY_OPTIONS}
                onChange={(option) => {
                  handleFrequencyChange(option);
                }}
                inPopup
              />
            </Col>
            <Col>
              {showDayPicker && (
                <>
                  {(schedule.frequency === ScheduleFrequency.WEEK ||
                    schedule.frequency === ScheduleFrequency.TWOWEEK) && (
                      <LabeledSelect
                        label="Day of the week"
                        value={weekDay || ""}
                        options={DAYS_OF_WEEK}
                        onChange={(option: any) =>
                          updater.setWeekDay(option.value)
                        }
                        inPopup
                      />
                    )}
                  {schedule.frequency === ScheduleFrequency.MONTH && (
                    <LabeledSelect
                      label="Day of the month"
                      value={monthDay || ""}
                      options={DAYS_OF_MONTH}
                      onChange={(option: any) =>
                        updater.setMonthDay(Number(option.value))
                      }
                      inPopup
                    />
                  )}
                  {schedule.frequency === ScheduleFrequency.QUARTER && (
                    <LabeledSelect
                      label="Day of the quarter"
                      value={quarterDay || ""}
                      options={QUARTER_FREQUENCY_OPTIONS}
                      onChange={(option: any) =>
                        updater.setQuarterDay(option.value)
                      }
                      inPopup
                    />
                  )}
                </>
              )}
            </Col>
          </Row>
          <Row>
            <Col>
              <EmailList
                label="Send To"
                value={sendTo}
                options={sendToOptions}
                onChange={(option: any) => updater.setSendTo(option.value)}
                inPopup
                isMulti={false}
                showDropdownIndicator
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );

  const successContent = success && (
    <AlertNotification alertType="success">
      <Text as="p" color="secondary3">
        <Text bold as="span" color="secondary3">
          {reportOption.label}
        </Text>
        &nbsp; has been scheduled! An email will be sent to {sendTo.email}&nbsp;
        {schedule.frequency === ScheduleFrequency.WEEK && "weekly"}
        {schedule.frequency === ScheduleFrequency.TWOWEEK && "every two weeks"}
        {schedule.frequency === ScheduleFrequency.MONTH && "monthly"}
        {schedule.frequency === ScheduleFrequency.QUARTER && "quarterly"}
        {schedule.frequency === ScheduleFrequency.DAY && "daily"}.
      </Text>
    </AlertNotification>
  );

  const customActions = success ? (
    <Button onClick={handleClose}>Close</Button>
  ) : (
    <>
      <Button secondary onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      {!isNew && (
        <Button
          disabled={loading}
          variant="error"
          onClick={() => setShowConfirm(true)}
        >
          Unschedule
        </Button>
      )}
      <Button disabled={!isValid || loading} onClick={saveScheduledReport}>
        {isNew ? "Schedule" : "Save"}
      </Button>
    </>
  );

  return (
    <FormPopup
      isNew={isNew}
      isEditable={isEditable}
      isValid={isValid}
      onClose={handleClose}
      title="Schedule Report"
      width="575px"
      height="450px"
      customActions={customActions}
      zIndex={BASE_ZINDEX + 400}
      overlayZIndex={BASE_ZINDEX + 300}
      onLeftArrowClick={handleLeftArrowClick}
    >
      {showConfirm && (
        <ConfirmUnschedulePopup
          zIndex={BASE_ZINDEX + 400}
          overlayZIndex={BASE_ZINDEX + 300}
          onClose={() => setShowConfirm(false)}
          onConfirm={() => {
            unscheduleReport().then(() => setShowConfirm(false));
            updater.reset();
          }}
          message="Are you sure you want to unschedule this report?"
        />
      )}
      {success ? successContent : loading ? loadingContent : formContent}
    </FormPopup>
  );
};

export default ScheduleReportPopup;
